import React, { useCallback, useMemo, useRef, useState } from 'react';
import s from './s.module.less';
import ResumeEntry from './components/ResumeEntry';
import AnalysisProcess from 'components/UniprofileResume/AnalysisProcess';
import AnalysisResult from 'components/UniprofileResume/AnalysisResult';
import Profile from './components/Profile';
import { EStep } from 'pages/UniprofileResume/types';
import { Button, Spin } from 'antd';
import useGetUniprofileResumeInfo from 'hooks/useGetUniprofileResumeInfo';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import { saveStep } from 'api/ppaOnboarding';
import { EPPAStep } from 'types/ppaOnboarding';

const ProfileEnrich = ({
    onNext,
    onBack,
    stepToSave,
}: {
    onNext: () => void;
    onBack: () => void;
    stepToSave?: string;
}) => {
    const [, loadingHomeInfo] = useProviderHomeInfo();
    const [, resumeInfoLoading] = useGetUniprofileResumeInfo();

    const [currentStep, setCurrentStep] = useState(EStep.RESUME_ENTRY);
    const [createType, setCreateType] = useState<number>(1);
    const [documentId, setDocumentId] = useState<string | true>();
    const [disableNextClick, setDisableNextClick] = useState(false);
    const profileRef = useRef<{ onSubmit: () => void }>(null);

    const handleSelectCreateUniprofileType = useCallback((v: number) => {
        setCreateType(v);
    }, []);

    const handleNextClick = useCallback(async () => {
        if (currentStep === EStep.RESUME_ENTRY && createType === 2) {
            setCurrentStep(EStep.PROFILE);
        } else if (currentStep === EStep.PROFILE) {
            await profileRef.current?.onSubmit();

            // 保存当前步骤 STEP_20
            try {
                const saveStepRes = await saveStep(stepToSave ?? EPPAStep.STEP_20);
                if (saveStepRes.error) {
                    // eslint-disable-next-line no-console
                    console.error('Failed to save step:', saveStepRes.error);
                    return;
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error saving step:', error);
                return;
            }

            onNext();
        } else {
            const step = currentStep + 1;
            if (step === EStep.ANALYSIS_PROCESS) {
                setDisableNextClick(true);
            }
            setCurrentStep(step);
        }
    }, [createType, currentStep, onNext, stepToSave]);

    const onClickBack = useCallback(() => {
        if (currentStep !== EStep.RESUME_ENTRY) {
            setCurrentStep(EStep.RESUME_ENTRY);
        } else {
            onBack();
        }
    }, [currentStep]);

    const onManualEnter = useCallback(() => {
        setCurrentStep(EStep.PROFILE);
    }, []);

    const handleUploadSuccess = useCallback((id?: string | true) => {
        setDocumentId(id);
    }, []);

    const handleAnalysisFinish = useCallback(() => {
        setDisableNextClick(false);
    }, []);

    const contentRender = useMemo(() => {
        let content = null;

        switch (currentStep) {
            case EStep.RESUME_ENTRY:
                content = (
                    <ResumeEntry
                        onUploadSuccess={handleUploadSuccess}
                        onClick={handleSelectCreateUniprofileType}
                        type={createType}
                        onManualEnter={onManualEnter}
                    />
                );
                break;
            case EStep.ANALYSIS_PROCESS:
                content = <AnalysisProcess onNext={handleNextClick} onFinish={handleAnalysisFinish} />;
                break;
            case EStep.ANALYSIS_RESULT:
                content = <AnalysisResult />;
                break;
            case EStep.PROFILE:
                content = <Profile ref={profileRef} isAva={createType === 1} />;
                break;
            default:
                content = null;
        }

        return content;
    }, [currentStep, handleSelectCreateUniprofileType, createType, handleUploadSuccess, handleAnalysisFinish]);

    if (resumeInfoLoading || loadingHomeInfo) {
        return <Spin />;
    }

    const disableNext = (currentStep === EStep.RESUME_ENTRY && createType === 1 && !documentId) || disableNextClick;

    return (
        <div className={s.wrap}>
            <div className={s.body}>
                <div className={s.content}>{contentRender}</div>
            </div>
            <div className="flex gap-4 mb-9 mt-6">
                <Button onClick={onClickBack} className="bg-white">
                    Back
                </Button>
                <Button type="primary" onClick={handleNextClick} disabled={disableNext}>
                    Next
                </Button>
            </div>
        </div>
    );
};

export default ProfileEnrich;
